import { Rational } from "@tento-core/rational";
import { RowId, UserId } from "@tento-lona/sheets";

import { RowNode } from "../rows/row-node.ts";
import type { JsonObject } from "@tento-lona/sheets";

export type EphemeralRowSeedDef = Readonly<{
  key: string;
  lookupKey?: string;
  type: string;
  label?: string;
  timeScale?: "day" | "week" | "month" | "year" | "custom";
  heightPx?: number;
  attributes?: JsonObject;
  metadata?: JsonObject;
}>;

const JOURNAL: EphemeralRowSeedDef = {
  key: ":~lona:journal",
  lookupKey: ":~lona:journal",
  type: "journal",
  label: "Journal",
  timeScale: "day",
  heightPx: 400,
};

const MOOD: EphemeralRowSeedDef = {
  key: "journal:mood",
  type: "number",
  label: "Mood",
  timeScale: "day",
  attributes: { unit: "scale1-5", prompt: "rate your mood (1-5)" },
};

const WAKE_UP_TIME: EphemeralRowSeedDef = {
  key: ":~lona:daily-habits:wake-up-time",
  lookupKey: ":~lona:daily-habits:wake-up-time",
  type: "text",
  label: "Wake up time",
  timeScale: "day",
  heightPx: 160,
  attributes: { prompt: "what time did you wake up?" },
};

const MEDITATION: EphemeralRowSeedDef = {
  key: ":~lona:daily-habits:meditation",
  lookupKey: ":~lona:daily-habits:meditation",
  type: "checkmark",
  label: "Meditation",
  timeScale: "day",
};

const MORNING_WALK: EphemeralRowSeedDef = {
  key: ":~lona:daily-habits:morning-walk",
  lookupKey: ":~lona:daily-habits:morning-walk",
  type: "checkmark",
  label: "Morning walk",
  timeScale: "day",
};

const WATER_INTAKE: EphemeralRowSeedDef = {
  key: ":~lona:daily-habits:water-intake",
  lookupKey: ":~lona:daily-habits:water-intake",
  type: "number",
  label: "Water intake",
  timeScale: "day",
  attributes: {
    unit: "glasses",
    prompt: "how many glasses of water did you drink?",
  },
};

const EXERCISE: EphemeralRowSeedDef = {
  key: ":~lona:daily-habits:exercise",
  lookupKey: ":~lona:daily-habits:exercise",
  type: "text",
  label: "Exercise",
  timeScale: "day",
  attributes: { prompt: "what exercise did you do today?" },
};

const TASKS: EphemeralRowSeedDef = {
  key: ":~lona:tasks",
  lookupKey: ":~lona:tasks",
  type: "tasks",
  label: "Tasks",
  timeScale: "day",
  metadata: { encoding: "task.v1" },
};

const POMODORO_EVENTS: EphemeralRowSeedDef = {
  key: ":~lona:calendar:pomodoro:events",
  lookupKey: ":~lona:calendar:pomodoro:events",
  type: "data",
  label: "Pomodoro",
  timeScale: "day",
  attributes: {
    description: "Track completed Pomodoro sessions as calendar events",
  },
};

export const EPHEMERAL_ROW_CATALOG: Readonly<Record<string, EphemeralRowSeedDef>> = {
  [JOURNAL.key]: JOURNAL,
  [MOOD.key]: MOOD,
  [WAKE_UP_TIME.key]: WAKE_UP_TIME,
  [MEDITATION.key]: MEDITATION,
  [MORNING_WALK.key]: MORNING_WALK,
  [WATER_INTAKE.key]: WATER_INTAKE,
  [EXERCISE.key]: EXERCISE,
  [TASKS.key]: TASKS,
  [POMODORO_EVENTS.key]: POMODORO_EVENTS,
};

export const BUILTIN_SHEET_EPHEMERAL_ROWS: Readonly<Record<string, readonly string[]>> = {
  ":~lona:home": [JOURNAL.key, TASKS.key],
  ":journal": [JOURNAL.key, MOOD.key],
  ":daily-habits": [
    WAKE_UP_TIME.key,
    MEDITATION.key,
    MORNING_WALK.key,
    WATER_INTAKE.key,
    EXERCISE.key,
  ],
  ":tasks": [TASKS.key],
  ":debug-pomodoro": [POMODORO_EVENTS.key],
};

export namespace EphemeralRowSeeder {
  export function make(
    sheetLookupKey: string,
    ownerId: UserId,
    forkedByLookupKey: ReadonlySet<string>,
    presentSeedKeys: ReadonlySet<string>,
  ): RowNode.DecomposableRow[] {
    const seeds = BUILTIN_SHEET_EPHEMERAL_ROWS[sheetLookupKey] ?? [];
    const rows: RowNode.DecomposableRow[] = [];

    for (let i = 0; i < seeds.length; i++) {
      const seedKey = seeds[i];
      const def = seedKey ? EPHEMERAL_ROW_CATALOG[seedKey] : null;
      if (!def) continue;

      if (presentSeedKeys.has(def.key)) continue;
      if (def.lookupKey && forkedByLookupKey.has(def.lookupKey)) continue;

      rows.push({
        id: def.lookupKey ?? RowId.deterministic(
          `builtin:${sheetLookupKey}:${def.key}`,
        ),
        ...(def.lookupKey != null ? { lookupKey: def.lookupKey } : {}),
        type: def.type,
        ownerId,
        timeScale: def.timeScale ?? "day",
        ...(def.label !== undefined ? { label: def.label } : {}),
        ...(def.heightPx !== undefined ? { heightPx: def.heightPx } : {}),
        ...(def.attributes !== undefined ? { attributes: def.attributes } : {}),
        ...(def.metadata !== undefined ? { metadata: def.metadata } : {}),
        position: Rational.nth(i),
      });
    }

    return rows;
  }
}
